import { Fragment } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Divider,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Link } from '@backstage/core-components';
import { EntityRefLink } from '@backstage/plugin-catalog-react';
import {
  eyebrowStyle,
  headingColor,
  linkColor,
  text,
} from '@univerus/plugin-products';
import { GfmMarkdownContent } from '../catalog/GfmMarkdownContent';
import { formatRelativeDate } from './formatRelativeDate';

/**
 * A release as both surfaces render it.
 *
 * The catalog fields are optional: the org-wide feed carries them so a row can
 * say which product shipped, while an entity's own tab leaves them out,
 * because repeating the entity you are already looking at is noise.
 *
 * Mirrors `ReleaseBase`/`ReleaseSummary` in
 * `packages/backend/src/plugins/githubReleasesApi.ts`.
 */
export interface ReleaseListItem {
  id: string;
  repoSlug: string;
  name: string;
  tagName: string;
  htmlUrl: string;
  publishedAt: string;
  prerelease: boolean;
  body: string;
  truncated: boolean;
  entityRef?: string;
  entityTitle?: string;
  owner?: string;
  ownerLabel?: string;
}

const useStyles = makeStyles(theme => ({
  // The list lives inside one card, so each row has to shed the Paper that
  // Accordion brings by default — otherwise every release renders as a card
  // inside a card.
  accordion: {
    boxShadow: 'none',
    backgroundColor: 'transparent',
    margin: 0,
    '&:before': {
      display: 'none',
    },
    '&$expanded': {
      margin: 0,
    },
  },
  expanded: {},
  summary: {
    padding: theme.spacing(0, 1, 0, 0),
    '&$expanded': {
      minHeight: 0,
    },
  },
  summaryContent: {
    margin: theme.spacing(2, 0),
    '&$expanded': {
      margin: theme.spacing(2, 0),
    },
  },
  details: {
    display: 'block',
    padding: theme.spacing(0, 0, 2, 0),
  },
  // Matches the Accordion rows so a release without notes sits on the same
  // baseline as one that expands.
  staticRow: {
    padding: theme.spacing(2, 0),
  },
  // Type follows the Products and homepage release cards: product as a teal
  // eyebrow, the release name as a navy title, tag and date as meta text.
  eyebrow: {
    ...eyebrowStyle(theme),
    marginBottom: theme.spacing(0.5),
  },
  title: {
    fontSize: text.lead,
    fontWeight: 600,
    lineHeight: 1.4,
    color: headingColor(theme),
  },
  meta: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5),
    fontSize: text.meta,
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
  },
  // Release notes are other people's markdown: headings authored for a
  // standalone page would otherwise tower over the release title here.
  notes: {
    fontSize: text.body,
    lineHeight: '23px',
    '& h1, & h2, & h3, & h4': {
      fontSize: theme.typography.body1.fontSize,
      fontWeight: theme.typography.fontWeightMedium,
      marginTop: theme.spacing(1.5),
      marginBottom: theme.spacing(0.5),
    },
    '& p:first-child': {
      marginTop: 0,
    },
    '& img': {
      maxWidth: '100%',
    },
  },
  detailLinks: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginTop: theme.spacing(1.5),
    fontSize: text.small,
    '& a': { color: linkColor(theme) },
  },
  prerelease: {
    height: theme.spacing(2.5),
  },
}));

/**
 * Release notes may contain repo-relative links and images, which would 404
 * when rendered outside the repo. Rewrite them onto GitHub.
 */
export function absoluteGithubUri(uri: string, repoSlug: string): string {
  if (/^(https?:|data:|#|mailto:)/.test(uri)) {
    return uri;
  }
  const cleanPath = uri.replace(/^\.?\//, '');
  return `https://github.com/${repoSlug}/blob/HEAD/${cleanPath}`;
}

/**
 * The scannable half of a row: what shipped, which version, and when — plus
 * which product, when the caller is showing more than one.
 *
 * Deliberately free of links: it doubles as the expand control, and an
 * interactive element nested in another one reads badly to assistive tech.
 */
const ReleaseHeadline = ({ release }: { release: ReleaseListItem }) => {
  const classes = useStyles();
  const published = formatRelativeDate(release.publishedAt);

  // Built as a list so the separators land between whatever is actually
  // present: an unparseable date would otherwise leave a stray "·".
  const meta: { key: string; text: string; title?: string }[] = [];
  // Many releases are named after their tag; repeating it under the title
  // says nothing new.
  if (release.tagName !== release.name) {
    meta.push({ key: 'tag', text: release.tagName });
  }
  if (published) {
    meta.push({
      key: 'published',
      text: published,
      title: new Date(release.publishedAt).toLocaleString(),
    });
  }

  return (
    <Box>
      {release.entityTitle && (
        <Typography className={classes.eyebrow}>
          {release.entityTitle}
        </Typography>
      )}
      <Typography component="h3" className={classes.title}>
        {release.name}
      </Typography>
      <Box className={classes.meta}>
        {meta.map((item, index) => (
          <span key={item.key} title={item.title}>
            {index > 0 ? `· ${item.text}` : item.text}
          </span>
        ))}
        {release.prerelease && (
          <Chip
            label="Pre-release"
            size="small"
            variant="outlined"
            className={classes.prerelease}
          />
        )}
      </Box>
    </Box>
  );
};

/**
 * Links shown with a release: GitHub always, and the catalog entity only when
 * the caller is not already sitting on it.
 */
const ReleaseLinks = ({ release }: { release: ReleaseListItem }) => {
  const classes = useStyles();

  return (
    <Box className={classes.detailLinks}>
      <Link to={release.htmlUrl}>
        {release.truncated ? 'Read the full release notes' : 'View on GitHub'}
      </Link>
      {release.entityRef && (
        <EntityRefLink entityRef={release.entityRef} hideIcon>
          {release.entityTitle ?? release.repoSlug}
        </EntityRefLink>
      )}
    </Box>
  );
};

/**
 * One release. Collapsed by default so a list of them stays scannable; the
 * notes and links live behind the expand.
 *
 * A release with no notes renders as a plain row instead of an Accordion —
 * an expand control that opens onto nothing is a dead control.
 */
export const ReleaseItem = ({ release }: { release: ReleaseListItem }) => {
  const classes = useStyles();

  if (!release.body) {
    return (
      <Box className={classes.staticRow}>
        <ReleaseHeadline release={release} />
        <ReleaseLinks release={release} />
      </Box>
    );
  }

  return (
    <Accordion
      // Elevation 0 and square: the theme borders and rounds every
      // elevation-1 Paper, which would box each row inside the list's card.
      elevation={0}
      square
      classes={{ root: classes.accordion, expanded: classes.expanded }}
      TransitionProps={{ unmountOnExit: true }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-label={`Show release notes for ${release.name}`}
        classes={{
          root: classes.summary,
          content: classes.summaryContent,
          expanded: classes.expanded,
        }}
      >
        <ReleaseHeadline release={release} />
      </AccordionSummary>

      <AccordionDetails classes={{ root: classes.details }}>
        <Box className={classes.notes}>
          <GfmMarkdownContent
            content={release.body}
            transformImageUri={src => absoluteGithubUri(src, release.repoSlug)}
            transformLinkUri={href => absoluteGithubUri(href, release.repoSlug)}
          />
        </Box>
        <ReleaseLinks release={release} />
      </AccordionDetails>
    </Accordion>
  );
};

/**
 * A list of releases as flat blocks separated by dividers, meant to sit inside
 * a single card. Shared by the Releases page and an entity's Releases tab so
 * the two cannot drift apart.
 */
export const ReleaseList = ({ releases }: { releases: ReleaseListItem[] }) => (
  <>
    {releases.map((release, index) => (
      <Fragment key={release.id}>
        {index > 0 && <Divider />}
        {/* Marks each row, so a caller can move focus to a given release. */}
        <div data-release-item>
          <ReleaseItem release={release} />
        </div>
      </Fragment>
    ))}
  </>
);
